import React, { useMemo } from 'react';
import Portal from '@leafygreen-ui/portal';
import { useIdAllocator } from '@leafygreen-ui/hooks';
import TabTitle from './TabTitle';
import { TabsProps } from './Tabs';

type InternalTabProps = Pick<TabsProps, 'as' | 'darkMode' | 'className'> & {
  child: React.ReactElement;
  onKeyDown: (e: KeyboardEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  isAnyTabFocused?: boolean;
  selected: boolean;
  tabRef: HTMLDivElement | null;
  panelRef: HTMLDivElement | null;
};

const InternalTab = React.memo(
  ({ child, selected, tabRef, panelRef, ...tabProps }: InternalTabProps) => {
    const { id: idProp, name } = child.props;

    const panelId = useIdAllocator({ prefix: 'tab-panel' });
    const tabId = useIdAllocator({ prefix: 'tab', id: idProp });

    const tab = (
      <TabTitle
        {...tabProps}
        selected={selected}
        id={tabId}
        aria-controls={panelId}
      >
        {name}
      </TabTitle>
    );

    const panel = useMemo(
      () =>
        React.cloneElement(child, {
          id: panelId,
          selected: selected,
          ['aria-labelledby']: tabId,
        }),
      [child, panelId, selected, tabId],
    );

    return (
      <>
        <Portal container={tabRef}>{tab}</Portal>
        <Portal container={panelRef}>{panel}</Portal>
      </>
    );
  },
);

InternalTab.displayName = 'InternalTab';

export default InternalTab;
